import React, { useState } from "react";

import {
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Lock as LockIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
  SwapHoriz as SwapHorizIcon,
  PictureAsPdf as PdfIcon,
  Description as ProgramIcon,
  CalendarMonth as CalendarIcon,
  ImageSearch as FlyerIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { HeaderNotifications } from "./HeaderNotifications";
import { apiClient } from "../../api/client";
import { ISchoolContactServiceResponse } from "../../api/PartnerPortalGatewayApi";
import { getRuntimeConfig } from "../../config/runtime-config";
import { useActionNotifications, useLoading, useNotification } from "../../contexts";
import { useTermDatesFromYear } from "../../hooks/useTermDates";
import { RootState } from "../../store";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setSelectedService } from "../../store/slices/serviceSlice";
import { CADialog } from "../ui";

interface HeaderProps {
  onSidebarToggle: () => void;
  title: string;
}

function Header({ onSidebarToggle }: HeaderProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [changeServiceDialogOpen, setChangeServiceDialogOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");

  const selectedService = useAppSelector((state: RootState) => state.service.selectedService);
  const availableServices = useAppSelector((state: RootState) => state.service.availableServices);
  const user = useAppSelector((state: RootState) => state.auth.user);

  // Notification contexts
  const { addNotification, updateNotification } = useActionNotifications();
  const { showLoading, hideLoading } = useLoading();
  const { showInfo, showSuccess, showError } = useNotification();

  // Get term dates for the current year
  const { data: termDates } = useTermDatesFromYear(selectedService?.serviceId, new Date().getFullYear());

  const displayName =
    user?.givenName && user?.familyName ? `${user.givenName} ${user.familyName}` : user?.name || "User Account";

  const initials =
    user?.givenName && user?.familyName ? `${user.givenName.charAt(0)}${user.familyName.charAt(0)}`.toUpperCase() : "U";

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  // Download handlers with notification tracking
  const handleDownloadDigitalPrograms = async () => {
    if (!selectedService?.serviceId) return;

    setUserMenuAnchor(null);

    const notificationId = addNotification({
      title: "Digital Programs",
      status: "running",
      message: "Downloading digital programs...",
    });

    try {
      showInfo("Requested digital programs.");
      showLoading("Downloading digital programs...");
      const response = await apiClient.documents.downloadCurrentDigitalPrograms(selectedService.serviceId);

      // Create blob and download
      const blob = new Blob([response.data], {
        type: response.headers?.["content-type"] || "application/octet-stream",
      });

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers?.["content-disposition"];
      let filename = "digital-programs.pdf";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      hideLoading();
      showSuccess("Digital programs downloaded successfully!");

      updateNotification(notificationId, {
        status: "completed",
        message: "Digital programs downloaded successfully!",
      });
    } catch (error: any) {
      hideLoading();

      let errorMessage = "Failed to download digital programs. Please try again.";
      try {
        if (error?.response?.text) {
          const responseText = await error.response.text();
          if (responseText) {
            errorMessage = responseText;
          }
        } else if (error?.response?.data) {
          errorMessage =
            typeof error.response.data === "string"
              ? error.response.data
              : error.response.data.message || error.response.data.title || errorMessage;
        } else if (error?.message) {
          errorMessage = error.message;
        }
      } catch (textError) {
        console.error("Error extracting response text:", textError);
        errorMessage = error?.message || errorMessage;
      }

      showError(errorMessage);
      console.error("Error downloading digital programs:", error);

      updateNotification(notificationId, {
        status: "error",
        message: errorMessage,
      });
    }
  };

  const handleDownloadTermReport = async () => {
    if (!selectedService?.serviceId) return;

    setUserMenuAnchor(null);

    const notificationId = addNotification({
      title: "Term Report",
      status: "running",
      message: "Downloading term report...",
    });

    try {
      showInfo("Requested term report.");
      const now = new Date();

      // Find the current term based on actual term dates
      let currentTerm: number | null = null;
      let currentYear = now.getFullYear();

      if (termDates && termDates.length > 0) {
        const currentTermData = termDates.find((term) => {
          const startDate = new Date(term.firstDayOfTerm.toDate());
          const endDate = new Date(term.lastDayOfTerm.toDate());
          return now >= startDate && now <= endDate;
        });

        if (currentTermData) {
          currentTerm = currentTermData.termNumber;
          currentYear = currentTermData.year;
        } else {
          const completedTerms = termDates.filter((term) => {
            const endDate = new Date(term.lastDayOfTerm.toDate());
            return now > endDate;
          });

          if (completedTerms.length > 0) {
            const mostRecentTerm = completedTerms.sort(
              (a, b) => b.lastDayOfTerm.toDate().getTime() - a.lastDayOfTerm.toDate().getTime(),
            )[0];
            currentTerm = mostRecentTerm.termNumber;
            currentYear = mostRecentTerm.year;
          }
        }
      }

      // Fallback to simple calculation if no term data available
      if (currentTerm === null) {
        const currentMonth = now.getMonth() + 1;
        if (currentMonth <= 3) {
          currentTerm = 1;
        } else if (currentMonth <= 6) {
          currentTerm = 2;
        } else if (currentMonth <= 9) {
          currentTerm = 3;
        } else {
          currentTerm = 4;
        }
        currentYear = now.getFullYear();
      }

      showLoading("Downloading term report...");

      const response = await apiClient.documents.getServiceTermReportDownload(
        selectedService.serviceId,
        currentTerm,
        currentYear,
        false,
      );

      // Create download link and trigger download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        response.fileName || `term-report-${selectedService.serviceId}-T${currentTerm}-${currentYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      hideLoading();
      showSuccess("Term report downloaded successfully!");

      updateNotification(notificationId, {
        status: "completed",
        message: "Term report downloaded successfully!",
      });
    } catch (error: any) {
      hideLoading();
      showError("Failed to download term report. Please try again.");
      console.error("Error downloading term report:", error);

      updateNotification(notificationId, {
        status: "error",
        message: "Failed to download term report. Please try again.",
      });
    }
  };

  const handleDownloadMonthReport = async () => {
    if (!selectedService?.serviceId) return;

    setUserMenuAnchor(null);

    const notificationId = addNotification({
      title: "Monthly Report",
      status: "running",
      message: "Downloading monthly report...",
    });

    try {
      showInfo("Requested monthly report.");

      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
      const month = lastMonth.getMonth() + 1;
      const year = lastMonth.getFullYear();

      showLoading("Downloading monthly report...");

      const response = await apiClient.documents.getServiceMonthReportDownload(
        selectedService.serviceId,
        month,
        year,
        false,
      );

      // Create download link and trigger download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        response.fileName ||
        `month-report-${selectedService.serviceId}-${year}-${month.toString().padStart(2, "0")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      hideLoading();
      showSuccess("Monthly report downloaded successfully!");

      updateNotification(notificationId, {
        status: "completed",
        message: "Monthly report downloaded successfully!",
      });
    } catch (error: any) {
      hideLoading();
      showError("Failed to download monthly report. Please try again.");
      console.error("Error downloading monthly report:", error);

      updateNotification(notificationId, {
        status: "error",
        message: "Failed to download monthly report. Please try again.",
      });
    }
  };

  const handleDownloadFlyer = async () => {
    if (!selectedService?.serviceId) return;

    setUserMenuAnchor(null);

    const notificationId = addNotification({
      title: "Service Flyer",
      status: "running",
      message: "Downloading service flyer...",
    });

    try {
      showInfo("Requested service flyer.");

      showLoading("Downloading service flyer...");
      const response = await apiClient.documents.downloadServiceFlyer(selectedService.serviceId);

      // Create blob and download
      const blob = new Blob([response.data], {
        type: response.headers?.["content-type"] || "application/pdf",
      });

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers?.["content-disposition"];
      let filename = `${selectedService.serviceId}_flyer.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      hideLoading();
      showSuccess("Service flyer downloaded successfully!");

      updateNotification(notificationId, {
        status: "completed",
        message: "Service flyer downloaded successfully!",
      });
    } catch (error: any) {
      hideLoading();

      let errorMessage = "Failed to download service flyer. Please try again.";
      try {
        if (error?.response?.text) {
          const responseText = await error.response.text();
          if (responseText) {
            errorMessage = responseText;
          }
        } else if (error?.response?.data) {
          errorMessage =
            typeof error.response.data === "string"
              ? error.response.data
              : error.response.data.message || error.response.data.title || errorMessage;
        } else if (error?.message) {
          errorMessage = error.message;
        }
      } catch (textError) {
        console.error("Error extracting response text:", textError);
        errorMessage = error?.message || errorMessage;
      }

      showError(errorMessage);
      console.error("Error downloading service flyer:", error);

      updateNotification(notificationId, {
        status: "error",
        message: errorMessage,
      });
    }
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    navigate("/logout");
  };

  const handleChangePassword = () => {
    handleUserMenuClose();
    const config = getRuntimeConfig();
    window.location.href = `${config.authUri}/Account/ChangePassword?clientId=${config.clientId}&returnUrl=${encodeURIComponent(window.location.origin)}`;
  };

  const handleOpenChangeServiceDialog = () => {
    handleUserMenuClose();
    setSelectedServiceId(selectedService?.serviceId?.toString() || "");
    setChangeServiceDialogOpen(true);
  };

  const handleCloseChangeServiceDialog = () => {
    setChangeServiceDialogOpen(false);
    setSelectedServiceId("");
  };

  const handleSaveService = () => {
    const service = availableServices.find(
      (s: ISchoolContactServiceResponse) => s.serviceId?.toString() === selectedServiceId,
    );
    if (service) {
      dispatch(setSelectedService(service));
    }
    handleCloseChangeServiceDialog();
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "#F1DDFF",
        color: "#333",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        elevation: 0,
        zIndex: 1100,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      <Toolbar
        sx={{
          width: "100%",
          maxWidth: "100%",
          minHeight: { xs: 56, sm: 64 },
          paddingX: { xs: 1, sm: 2 },
          boxSizing: "border-box",
          gap: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          overflow: "visible",
        }}
      >
        {/* Heart Menu Button */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={onSidebarToggle}
          sx={{
            marginRight: 2,
            "&:hover": {
              backgroundColor: "rgba(208, 141, 255, 0.1)",
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Page Title */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            fontFamily: "ABC Whyte",
            fontWeight: 600,
            color: "#333",
            fontSize: {
              xs: "1rem", // 16px on mobile
              sm: "1.125rem", // 18px on small tablets
              md: "1.25rem", // 20px on desktop (default h6)
            },
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Hello {selectedService?.serviceName || "School"}
        </Typography>

        {/* User Menu */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            overflow: "visible",
            flexShrink: 0,
          }}
        >
          {/* Header Notifications */}
          <HeaderNotifications />

          <IconButton
            onClick={handleUserMenuOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "#333",
              padding: "4px 8px",
              "&:hover": {
                backgroundColor: "rgba(208, 141, 255, 0.1)",
              },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                minWidth: 36,
                minHeight: 36,
                borderRadius: "50%",
                backgroundColor: "#D08DFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {initials}
            </Box>
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    fontWeight: 500,
                    fontFamily: "ABC Whyte",
                    color: "#333",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </Typography>
                <ExpandMoreIcon />
              </Box>
            )}
          </IconButton>

          {/* User dropdown menu */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: "8px",
                minWidth: "200px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              },
              "& .MuiList-root": {
                padding: 0,
              },
            }}
          >
            {/* User Info Header */}
            <Box
              sx={{
                padding: "12px 16px",
                backgroundColor: "#F1DDFF",
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user?.email || ""}
                </Typography>
              </Box>
            </Box>

            {/* <MenuItem onClick={handleDownloadDigitalPrograms} sx={{ gap: 1 }}>
              <ProgramIcon />
              Digital Programs
            </MenuItem> */}
            <MenuItem onClick={handleDownloadTermReport} sx={{ gap: 1 }}>
              <PdfIcon />
              Term Report PDF
            </MenuItem>
            <MenuItem onClick={handleDownloadMonthReport} sx={{ gap: 1 }}>
              <CalendarIcon />
              Monthly Report
            </MenuItem>
            <MenuItem onClick={handleDownloadFlyer} sx={{ gap: 1 }}>
              <FlyerIcon />
              Service Flyer
            </MenuItem>

            <Divider sx={{ margin: 0 }} />
            <MenuItem onClick={handleOpenChangeServiceDialog} sx={{ gap: 1 }}>
              <SwapHorizIcon />
              Change Service
            </MenuItem>
            <MenuItem onClick={handleChangePassword} sx={{ gap: 1 }}>
              <AccountCircleIcon />
              Change Password
            </MenuItem>
            <MenuItem onClick={handleUserMenuClose} sx={{ gap: 1 }}>
              <LockIcon />
              Lock Screen
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ gap: 1 }}>
              <ExitToAppIcon />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Change Service Dialog */}
      <CADialog
        open={changeServiceDialogOpen}
        onClose={handleCloseChangeServiceDialog}
        title="Change Service"
        maxWidth="sm"
        onConfirm={handleSaveService}
        confirmText="Save"
        confirmDisabled={!selectedServiceId}
      >
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="change-service-select-label">Service</InputLabel>
          <Select
            labelId="change-service-select-label"
            id="change-service-select"
            value={selectedServiceId}
            label="Service"
            onChange={(e) => setSelectedServiceId(e.target.value as string)}
          >
            {availableServices.map((service: ISchoolContactServiceResponse) => (
              <MenuItem key={service.serviceId} value={service.serviceId?.toString()}>
                {service.serviceName}
                {service.positionName && ` - ${service.positionName}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CADialog>
    </AppBar>
  );
}

export default Header;
